import React, { CSSProperties, ReactElement, useCallback, useMemo } from "react";
import { TimelineGroupProps } from "./TimelineGroup.types";
import { TimelineRow } from "../TimelineRow";
import { UnstyledButton } from "src/ui";
import { CaptionBody, SmallBody } from "src/components/Text";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { Conditional } from "src/components/Conditional";
import classNames from "classnames";
import { localize } from "src/l10n";
import { plannerStyling } from "../utils";

function TimelineGroup({
    axisWidth,
    displayGroupHeader,
    group,
    hasWriteAccess,
    cumulativeIndex,
    offset,
    onBarClick,
    onBarGroupClick,
    onCategoryUpdate,
    onGroupClick,
    onItemDurationChange,
    onDisplayedItemsChange,
    onRailClick,
    timelineDuration,
    timelineMode,
    timelineSize,
    todayTime,
    positioning,
    boardId
}: TimelineGroupProps): ReactElement {
    const categories = group.categories || [];

    const onAxisClicked = useCallback(
        () => onGroupClick?.(group),
        [group, onGroupClick],
    );

    const categoryStyle = useMemo<CSSProperties>(() => ({
        top: `${offset}px`,
    }), [axisWidth, offset]);

    const labelStyle = useMemo<CSSProperties>(() => ({
        height: `47px`,
        overflow: axisWidth > 0 ? undefined : "hidden",
        padding: axisWidth > 0 ? undefined : 0,
        top: `${offset}px`,
        width: `${axisWidth}px`,
    }), [axisWidth, offset]);

    return (
        <div
            className={classNames("TimelineGroup", {
                "expanded": group.expanded,
            })}
            style={categoryStyle}
        >
            <Conditional condition={displayGroupHeader}>
                <div className="TimelineGroup-axisLabel" style={labelStyle}>
                    <UnstyledButton className="TimelineGroup-axisButton" onClick={onAxisClicked}>
                        <SmallBody color="contentDark" weight="medium">
                            {group.name}
                        </SmallBody>
                        <Visage2Icon className="TimelineGroup-axisIcon" color="spintrGrey" icon="arrow-up-2" size="medium" />
                    </UnstyledButton>
                </div>
            </Conditional>
            <Conditional condition={group.expanded}>   
                <div className="TimelineGroup-items">
                    {categories.map((category, index) => {
                        const rowStyle: CSSProperties = {
                            height: positioning[boardId].nodes[category.key].height,
                            top: positioning[boardId].nodes[category.key].topOffset,
                        };

                        return (
                            <TimelineRow
                                axisWidth={axisWidth}
                                boardId={Number(group.key)}
                                category={category} 
                                hasWriteAccess={hasWriteAccess}
                                key={category.key}
                                onBarClick={onBarClick}
                                onBarGroupClick={onBarGroupClick}
                                onCategoryUpdate={onCategoryUpdate}
                                onItemDurationChange={onItemDurationChange}
                                onRailClick={onRailClick}
                                rowIndex={index}
                                style={rowStyle}
                                timelineDuration={timelineDuration}
                                timelineMode={timelineMode}
                                timelineWidth={timelineSize}
                                todayTime={todayTime}
                                color={group.color}
                            />
                        );
                    })}
                </div>
            </Conditional>
        </div>
    );
}

export default TimelineGroup;
